import { Suspense, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import { CompanyTabs } from '@components/company-tabs';
import {
  BILLING_PAGE_ID,
  EVENT_DEFAULTS_PAGE_ID,
  INFO_PAGE_ID,
  INTEGRATIONS_PAGE_ID,
  USERS_PAGE_ID,
  VENUES_PAGE_ID,
  WIDGET_PAGE_ID,
} from '@components/company-tabs/constants';
import { IconArrow } from '@components/icons/icon-arrow';
import { ImageCropWithDescription } from '@components/image-crop-with-description';
import { NavigationBackButton } from '@components/navigation-back-button';
import { PLACEHOLDER_IMAGE } from '@constants/global';
import { EVENT_LIST } from '@constants/routes';
import { CompanyClubContext } from '@contexts/company-club/company-club-context';
import { LAYOUT_WITHOUT_SIDEBAR } from '@contexts/layout/layout.type';
import { LayoutContext } from '@contexts/layout/layout-context';
import { COMPANY_SETTINGS_IMAGE_INPUT_NAME } from '@domain/company-settings';
import { useCompanyTabs } from '@hooks/use-company-tabs';

import { CompanySettingsOutletContext } from './company-settings-layout.type';

const getActiveTabFromPath = (pathname: string): string => {
  if (pathname.endsWith('/info')) return INFO_PAGE_ID;
  if (pathname.endsWith('/event-defaults')) return EVENT_DEFAULTS_PAGE_ID;
  if (pathname.endsWith('/integrations')) return INTEGRATIONS_PAGE_ID;
  if (/\/users(\/|$)/.test(pathname)) return USERS_PAGE_ID;
  if (pathname.endsWith('/widget')) return WIDGET_PAGE_ID;
  if (pathname.endsWith('/billing')) return BILLING_PAGE_ID;
  if (pathname.endsWith('/venues')) return VENUES_PAGE_ID;
  return INFO_PAGE_ID;
};

export const CompanySettingsLayout = () => {
  const { companyState } = useContext(CompanyClubContext);
  const { setLayout } = useContext(LayoutContext);
  const { companyId, clubSlug } = useParams<{ companyId: string; clubSlug: string }>();
  const location = useLocation();
  const { tabs } = useCompanyTabs();

  const [logoEditable, setLogoEditable] = useState(false);
  const profileImageCallbackRef = useRef<((blob: Blob | null) => void) | undefined>();

  useEffect(() => {
    setLayout({ type: LAYOUT_WITHOUT_SIDEBAR, topElement: undefined });
  }, [setLayout]);

  const activeTabId = useMemo(() => getActiveTabFromPath(location.pathname), [location.pathname]);

  const eventListPath =
    companyId && clubSlug
      ? EVENT_LIST.replace(':companyId', companyId).replace(':clubSlug', clubSlug).replace(':view', 'upcoming')
      : '';

  const logoUrl = companyState.logoUrl ? companyState.logoUrl : PLACEHOLDER_IMAGE;

  const headerMethods = useForm<{ [COMPANY_SETTINGS_IMAGE_INPUT_NAME]: string }>({
    defaultValues: { [COMPANY_SETTINGS_IMAGE_INPUT_NAME]: logoUrl },
    mode: 'onChange',
  });

  useEffect(() => {
    headerMethods.setValue(COMPANY_SETTINGS_IMAGE_INPUT_NAME, logoUrl);
  }, [logoUrl, headerMethods]);

  const outletContext: CompanySettingsOutletContext = useMemo(
    () => ({
      setLogoEditable,
      setProfileImageCallback: (fn: ((blob: Blob | null) => void) | undefined) => {
        profileImageCallbackRef.current = fn;
      },
    }),
    [setLogoEditable]
  );

  return (
    <div className='px-4 md:px-0 pb-10'>
      <div className='flex justify-center'>
        <div className='w-full md:w-212'>
          <NavigationBackButton
            forceDefaultRoute={true}
            defaultRoute={eventListPath}
            className='rounded-full w-max mb-4 md:mb-6'>
            <IconArrow className='w-5 h-5 rotate-180 text-primary' />
          </NavigationBackButton>
        </div>
      </div>
      <div className='flex justify-center'>
        <div className='w-full md:w-212'>
          <header className='pb-12'>
            <FormProvider {...headerMethods}>
              <ImageCropWithDescription
                id={COMPANY_SETTINGS_IMAGE_INPUT_NAME}
                name={COMPANY_SETTINGS_IMAGE_INPUT_NAME}
                title={companyState.commercialName}
                subtitle={companyState.name}
                isImgEditable={logoEditable}
                isHorizontal={false}
                setProfileImage={(blob) => {
                  profileImageCallbackRef.current?.(blob instanceof Blob ? blob : null);
                }}
              />
              <CompanyTabs tabs={tabs} initialTabId={activeTabId} />
            </FormProvider>
          </header>
        </div>
      </div>
      <Suspense>
        <Outlet context={outletContext} />
      </Suspense>
    </div>
  );
};
